import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLookupsContext } from "hooks/useContext";
import CardCheckbox from "components/cardCheckbox";
import TimePicker from "components/timePicker";
import Select from "components/select";
import useTime from "hooks/useTime";
import {
  availableDaysOptionsFullDays,
  timesOptions,
  availableDaysNumberMapperFullDays,
  availableDaysOptionsMapperFullDays,
} from "utils/constantValues";

function EditAvailability({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const { availability, timezone, country } = userDetails;
  const countryId = country?.id;
  const { timezones, getTimeZones } = useLookupsContext(true);

  useEffect(() => {
    if (countryId) {
      getTimeZones(countryId);
    }
  }, [countryId]);

  const [availableDays, setAvailableDays] = useState(
    availability?.days?.length
      ? availability?.days.map((dayNum) => {
          return availableDaysNumberMapperFullDays[dayNum];
        })
      : []
  );
  const defaultsValues = {
    timezone: timezone?.id ?? "",
    availability: {
      days: availability?.days ?? [],
      time: {
        from: availability?.time?.from ?? "",
        to: availability?.time?.to ?? "",
      },
    },
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });

  const {
    onMeridiemClick,
    meridiem,
    timeFrom,
    setTimeFrom,
    timeTo,
    setTimeTo,
    convertTimeTo24h,
  } = useTime(availability?.time);

  const timezonesOptions = timezones.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const onSubmit = (values) => {
    // Pass both availability and timezone data
    onSave({
      availability: values.availability,
      timezone: values.timezone,
    });
  };

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7] ">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("availabilityTitle")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-col self-stretch p-[24px] gap-[20px]">
        <div className="flex flex-wrap gap-[5px] items-center">
          {availableDaysOptionsFullDays.map((title) => {
            const isActive = availableDays.includes(title);
            return (
              <Fragment key={title}>
                <CardCheckbox
                  title={title}
                  isActive={isActive}
                  onClick={() => {
                    const availability = watch("availability");
                    if (availableDays.includes(title)) {
                      //remove
                      const newAvailableDays = availableDays.filter(
                        (elem) => elem !== title
                      );
                      setValue("availability", {
                        ...availability,
                        days: newAvailableDays.map(
                          (day) => availableDaysOptionsMapperFullDays[day]
                        ),
                      });
                      setAvailableDays(newAvailableDays);
                    } else {
                      //add
                      const newAvailableDays = [...availableDays, title];
                      setValue("availability", {
                        ...availability,
                        days: newAvailableDays.map(
                          (day) => availableDaysOptionsMapperFullDays[day]
                        ),
                      });
                      setAvailableDays(newAvailableDays);
                    }
                  }}
                  hideCheckIcon
                  containerClassName="flex h-8 px-3 py-2 justify-center items-center rounded-md cursor-pointer"
                  titleClassName={`text-center font-semibold text-base leading-6 font-[General_Sans] ${
                    isActive ? "text-[#1E4A6B]" : "text-[#3F3F46]"
                  }`}
                  customActiveClassName="flex h-8 px-3 py-2 justify-center items-center rounded-md border border-[#1E4A6B] bg-[#F3F5F7]"
                />
              </Fragment>
            );
          })}
        </div>
        <div className="flex flex-wrap - gap-[20px] self-stretch">
          <Select
            label={t("timeZone")}
            placeholder={t("placeholderSelect", {
              field: t("timeZone").toLowerCase(),
            })}
            name={"timezone"}
            containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
            isDisabled={timezones.length <= 0}
            value={
              watch("timezone")
                ? timezonesOptions.find((elem) => elem.id === watch("timezone"))
                : null
            }
            onChange={(e) => {
              setValue("timezone", e.value);
            }}
            register={register}
            options={timezonesOptions}
          />

          <TimePicker
            label={t("timeFromTitle")}
            placeholder={`HH:MM - PM/AM`}
            containerClassName="flex flex-col gap-[10px] min-w-[250px] lg-max:min-w-full"
            name={"timeFrom"}
            value={
              timeFrom
                ? {
                    ...timeFrom,
                    label: `${timeFrom.label} ${timeFrom.value.split(" ")[1]}`,
                  }
                : null
            }
            onChange={(e) => {
              const availability = watch("availability");
              setValue("availability", {
                ...availability,
                time: {
                  ...availability.time,
                  from: convertTimeTo24h(e.value),
                  to: "",
                },
              });
              setTimeFrom(e);
              setTimeTo(null);
            }}
            options={timesOptions.map((el) => ({
              ...el,
              value: `${el.value} ${meridiem.from}`,
            }))}
            onMeridiemClick={(chosenMeridiem) =>
              onMeridiemClick(chosenMeridiem, "from")
            }
            meridiem={meridiem.from}
          />

          <TimePicker
            label={t("timeToTitle")}
            containerClassName="flex flex-col gap-[10px] min-w-[250px] lg-max:min-w-full"
            placeholder={`HH:MM - PM/AM`}
            name={"timeTo"}
            isRequired={!timeFrom ? false : true}
            register={register}
            registerArgs={{
              required:
                !timeFrom || timeTo?.value
                  ? false
                  : t("errorField", { field: t("timeToTitle") }),
            }}
            errorMessage={
              !timeFrom ? "" : errors.timeTo ? errors.timeTo.message : ""
            }
            isDisabled={!timeFrom}
            value={
              timeTo
                ? {
                    ...timeTo,
                    label: `${timeTo.label} ${timeTo.value.split(" ")[1]}`,
                  }
                : null
            }
            onChange={(e) => {
              const availability = watch("availability");
              setValue("availability", {
                ...availability,
                time: { ...availability.time, to: convertTimeTo24h(e.value) },
              });
              setValue("timeTo", e.value);
              setTimeTo(e);
            }}
            options={timesOptions.map((el) => ({
              ...el,
              value: `${el.value} ${meridiem.to}`,
            }))}
            onMeridiemClick={(chosenMeridiem) =>
              onMeridiemClick(chosenMeridiem, "to")
            }
            meridiem={meridiem.to}
          />
        </div>
      </div>

      {/* <div className="flex flex-row md-max:flex-col justify-between md:max:justify-start flex-wrap flex-1 gap-[20px]">
        <div className={"flex flex-col gap-[10px]"}>
          <div className="text-primary font-semibold text-[16px]">
            {t("availableDaysTitle")}
          </div>

       
        </div>
        <div className="flex flex-wrap flex-1 gap-[16px]">
        
        </div>
      </div> */}
    </form>
  );
}

EditAvailability.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditAvailability;
